<template>
  <v-card v-if="hasAnyData" class="live-card mb-3">
    <div class="live-card__head">
      <span class="live-card__dot" />
      <span class="live-card__title">Live im Shop</span>
      <div class="live-card__chips">
        <button
          v-for="option in shopOptions"
          :key="option.code"
          type="button"
          class="live-card__chip"
          :class="{ 'live-card__chip--active': selected === option.code }"
          @click="selected = option.code"
        >
          {{ option.label }}
          <span v-if="activeByShop[option.code] || option.code === 'ALL'" class="live-card__chip-count">
            {{ option.code === 'ALL' ? totalActive : activeByShop[option.code] }}
          </span>
        </button>
      </div>
      <div class="live-card__mode">
        <button
          type="button"
          class="live-card__chip"
          :class="{ 'live-card__chip--active': mode === 'now' }"
          @click="mode = 'now'"
        >Jetzt (10 Min)</button>
        <button
          type="button"
          class="live-card__chip"
          :class="{ 'live-card__chip--active': mode === 'today' }"
          @click="mode = 'today'"
        >Heute</button>
      </div>
      <v-spacer />
      <span class="live-card__hint">{{ mode === 'today' ? (refLabel || "aktualisiert sich automatisch") : "rollierendes 10-Minuten-Fenster" }}</span>
    </div>

    <div class="live-card__body">
      <!-- Besucher jetzt + Verlauf -->
      <div class="live-card__now">
        <div class="live-card__big">{{ shownActive }}</div>
        <div class="live-card__big-label">Besucher jetzt</div>
        <div class="live-card__now-rows">
          <div class="live-card__now-row">
            <v-icon x-small class="mr-1">mdi-cart-outline</v-icon>
            {{ shownActiveCarts }} mit Warenkorb
          </div>
        </div>
        <svg v-if="sparkPoints" class="live-card__spark" viewBox="0 0 120 34" preserveAspectRatio="none">
          <polyline :points="sparkPoints" fill="none" stroke="#27a950" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
        </svg>
        <div class="live-card__spark-label">Seitenaufrufe · letzte 60 Min</div>
      </div>

      <!-- Funnel heute -->
      <div class="live-card__funnel">
        <!-- Raten-Zeile immer rendern (ggf. leer), damit alle Boxen gleich hoch sind -->
        <div v-for="step in funnel" :key="step.label" class="live-card__step">
          <div class="live-card__step-value">
            {{ step.value }}
            <span
              v-if="step.delta"
              class="live-card__step-delta"
              :class="step.delta.up ? 'live-card__step-delta--up' : 'live-card__step-delta--down'"
            >{{ step.delta.pct }}</span>
          </div>
          <div class="live-card__step-label">{{ step.label }}</div>
          <div class="live-card__step-rate">{{ step.rate || " " }}</div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import {
  collection,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../../../fb";

const SHOPS = [
  { code: "AQ_DE", label: "DE" },
  { code: "AQ_FR", label: "FR" },
  { code: "AQ_ES", label: "ES" },
  { code: "OH_US", label: "OhMy" },
];
const ACTIVE_WINDOW_MS = 5 * 60 * 1000; // Ping in den letzten 5 Min = aktiv
const RESUBSCRIBE_MS = 60 * 1000; // zeitbasierte Queries: Grenze jede Minute neu setzen

/** Tages-Schlüssel in Europe/Berlin (identisch zum Backend) */
const berlinDayKey = () =>
  new Date().toLocaleString("sv", { timeZone: "Europe/Berlin" }).slice(0, 10);

export default {
  name: "LiveStatsCard",
  data() {
    return {
      selected: "ALL",
      mode: "now", // 'now' = letzte 10 Minuten (Standard) | 'today' = Tages-Funnel
      activeByShop: {},
      activeCartsByShop: {},
      minutesByShop: {},
      todayByShop: {},
      // "gleiche Uhrzeit letzte Woche" (Fallback: gestern): {shop: {sessions,carts,checkouts,orders}}
      refByShop: {},
      refLabel: null,
      unsubs: [],
      timer: null,
    };
  },
  computed: {
    shopOptions() {
      return [{ code: "ALL", label: "Alle" }, ...SHOPS];
    },
    selectedShops() {
      return this.selected === "ALL" ? SHOPS.map((s) => s.code) : [this.selected];
    },
    totalActive() {
      return SHOPS.reduce((sum, s) => sum + (this.activeByShop[s.code] || 0), 0);
    },
    shownActive() {
      return this.selectedShops.reduce((sum, code) => sum + (this.activeByShop[code] || 0), 0);
    },
    hasAnyData() {
      return (
        this.totalActive > 0 ||
        Object.keys(this.todayByShop).length > 0 ||
        Object.values(this.minutesByShop).some((m) => m.length)
      );
    },
    shownActiveCarts() {
      return this.selectedShops.reduce(
        (sum, code) => sum + (this.activeCartsByShop[code] || 0), 0
      );
    },
    /** Checkouts/Bestellungen der letzten 10 Minuten (aus den Minuten-Buckets) */
    last10() {
      const cutoff = Date.now() - 10 * 60 * 1000;
      const result = { sessions: 0, carts: 0, checkouts: 0, orders: 0 };
      this.selectedShops.forEach((code) => {
        (this.minutesByShop[code] || []).forEach((b) => {
          if (!b.ts || b.ts.toMillis() < cutoff) return;
          result.sessions += b.sessions || 0;
          result.carts += b.carts || 0;
          result.checkouts += b.checkouts || 0;
          result.orders += b.orders || 0;
        });
      });
      return result;
    },
    funnel() {
      if (this.mode === "now") {
        const t = this.last10;
        return [
          { label: "Neue Sessions", value: t.sessions, rate: null, delta: null },
          { label: "Warenkörbe", value: t.carts, rate: null, delta: null },
          { label: "Checkouts", value: t.checkouts, rate: null, delta: null },
          { label: "Bestellungen", value: t.orders, rate: null, delta: null },
        ];
      }
      const sum = (obj, key) =>
        this.selectedShops.reduce((acc, code) => acc + ((obj[code] || {})[key] || 0), 0);
      const t = {
        sessions: sum(this.todayByShop, "sessions"),
        carts: sum(this.todayByShop, "carts"),
        checkouts: sum(this.todayByShop, "checkouts"),
        orders: sum(this.todayByShop, "orders"),
      };
      const ref = {
        sessions: sum(this.refByShop, "sessions"),
        carts: sum(this.refByShop, "carts"),
        checkouts: sum(this.refByShop, "checkouts"),
        orders: sum(this.refByShop, "orders"),
      };
      const rate = (a, b) =>
        b > 0 ? `${((a / b) * 100).toFixed(1).replace(".", ",")} %` : null;
      // Delta vs. Referenz (gleiche Uhrzeit): erst ab sinnvoller Basis zeigen
      const delta = (now, before) => {
        if (!this.refLabel || before < 3) return null;
        const pct = ((now - before) / before) * 100;
        return { pct: `${pct >= 0 ? "+" : ""}${pct.toFixed(0)} %`, up: pct >= 0 };
      };
      return [
        { label: "Sessions heute", value: t.sessions, rate: null, delta: delta(t.sessions, ref.sessions) },
        { label: "Warenkörbe", value: t.carts, rate: rate(t.carts, t.sessions), delta: delta(t.carts, ref.carts) },
        { label: "Checkouts", value: t.checkouts, rate: rate(t.checkouts, t.carts), delta: delta(t.checkouts, ref.checkouts) },
        { label: "Bestellungen", value: t.orders, rate: rate(t.orders, t.sessions), delta: delta(t.orders, ref.orders) },
      ];
    },
    sparkPoints() {
      // Pageviews je Minute über die ausgewählten Shops summiert
      const byMinute = {};
      let any = false;
      this.selectedShops.forEach((code) => {
        (this.minutesByShop[code] || []).forEach((b) => {
          if (!b.ts) return;
          any = true;
          const key = Math.floor(b.ts.toMillis() / 60000);
          byMinute[key] = (byMinute[key] || 0) + (b.pageviews || 0);
        });
      });
      if (!any) return null;
      const nowMin = Math.floor(Date.now() / 60000);
      const series = [];
      for (let i = 59; i >= 0; i--) series.push(byMinute[nowMin - i] || 0);
      const max = Math.max(...series, 1);
      return series
        .map((v, i) => `${(i / 59) * 120},${32 - (v / max) * 28}`)
        .join(" ");
    },
  },
  mounted() {
    this.subscribe();
    this.timer = setInterval(this.subscribe, RESUBSCRIBE_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    this.unsubscribeAll();
  },
  methods: {
    unsubscribeAll() {
      this.unsubs.forEach((u) => u && u());
      this.unsubs = [];
    },
    /** Referenz "gleiche Uhrzeit": Stunden-Buckets des Referenztags aufsummieren,
     *  laufende Stunde anteilig (Minuten-Fortschritt). Bevorzugt -7 Tage,
     *  Fallback -1 Tag, sonst keine Badges. */
    async fetchReference() {
      const now = new Date();
      const berlin = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Berlin" }));
      const curHour = berlin.getHours();
      const fraction = berlin.getMinutes() / 60;
      const dayKey = (daysBack) => {
        const d = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
        return d.toLocaleString("sv", { timeZone: "Europe/Berlin" }).slice(0, 10);
      };

      for (const daysBack of [7, 1]) {
        const day = dayKey(daysBack);
        const ref = {};
        let any = false;
        for (const { code } of SHOPS) {
          try {
            const snap = await getDocs(
              query(
                collection(db, `liveStats/${code}/hours`),
                where(documentId(), ">=", `${day}-00`),
                where(documentId(), "<=", `${day}-23`)
              )
            );
            const totals = { sessions: 0, carts: 0, checkouts: 0, orders: 0 };
            snap.forEach((d) => {
              const hour = parseInt(d.id.slice(11, 13), 10);
              if (hour > curHour) return;
              const factor = hour === curHour ? fraction : 1;
              const v = d.data();
              for (const key of Object.keys(totals)) totals[key] += (v[key] || 0) * factor;
            });
            if (snap.size > 0) any = true;
            ref[code] = {
              sessions: Math.round(totals.sessions),
              carts: Math.round(totals.carts),
              checkouts: Math.round(totals.checkouts),
              orders: Math.round(totals.orders),
            };
          } catch (e) {
            // still: ohne Referenz einfach keine Badges
          }
        }
        if (any) {
          this.refByShop = ref;
          this.refLabel = daysBack === 7 ? "vs. Vorwoche, gleiche Uhrzeit" : "vs. gestern, gleiche Uhrzeit";
          return;
        }
      }
      this.refByShop = {};
      this.refLabel = null;
    },
    subscribe() {
      this.unsubscribeAll();
      this.fetchReference();
      const silent = () => {}; // fehlende Rechte/Daten: Karte bleibt einfach leer

      SHOPS.forEach(({ code }) => {
        // Aktive Besucher: Sessions mit lastSeen im 5-Min-Fenster
        this.unsubs.push(
          onSnapshot(
            query(
              collection(db, `liveStats/${code}/sessions`),
              where("lastSeen", ">", new Date(Date.now() - ACTIVE_WINDOW_MS))
            ),
            (snap) => {
              this.$set(this.activeByShop, code, snap.size);
              // "aktive Warenkörbe": Sessions, deren letzter Ping Artikel im Cart meldete
              let withCart = 0;
              snap.forEach((d) => { if ((d.data().cart || 0) > 0) withCart++; });
              this.$set(this.activeCartsByShop, code, withCart);
            },
            silent
          )
        );

        // Minuten-Buckets der letzten 60 Min (Sparkline)
        this.unsubs.push(
          onSnapshot(
            query(
              collection(db, `liveStats/${code}/minutes`),
              where("ts", ">", new Date(Date.now() - 60 * 60 * 1000)),
              orderBy("ts")
            ),
            (snap) =>
              this.$set(this.minutesByShop, code, snap.docs.map((d) => d.data())),
            silent
          )
        );

        // Tages-Funnel (heute, Berlin-Zeit)
        this.unsubs.push(
          onSnapshot(
            doc(db, `liveStats/${code}/days/${berlinDayKey()}`),
            (snap) => {
              if (snap.exists()) this.$set(this.todayByShop, code, snap.data());
            },
            silent
          )
        );
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.live-card {
  border-radius: 14px !important;
  padding: 14px 18px;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;

    @media (max-width: 700px) {
      gap: 6px;
      margin-bottom: 6px;
    }
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #27a950;
    animation: live-pulse 1.6s ease-in-out infinite;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
  }

  &__chips {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 2px 10px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);

    @media (max-width: 700px) {
      font-size: 11px;
      padding: 2px 8px;
      gap: 4px;
    }

    &--active {
      background: #1f2937;
      border-color: #1f2937;
      color: #fff;
    }
  }

  &__mode {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: 8px;
    padding-left: 10px;
    border-left: 1px solid rgba(0, 0, 0, 0.08);

    @media (max-width: 700px) {
      margin-left: 0;
      padding-left: 0;
      border-left: none;
    }
  }

  &__chip-count {
    font-variant-numeric: tabular-nums;
    opacity: 0.75;
  }

  &__hint {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.35);

    /* Mobil: Hinweistext kostet eine ganze Zeile — weg damit */
    @media (max-width: 700px) {
      display: none;
    }
  }

  &__body {
    display: flex;
    gap: 28px;
    align-items: stretch;

    @media (max-width: 700px) {
      flex-direction: column;
      gap: 14px;
    }
  }

  &__now {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
  }

  &__big {
    font-size: 34px;
    font-weight: 800;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }

  &__big-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 6px;
  }

  &__now-rows {
    margin: 4px 0 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__now-row {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    white-space: nowrap;

    .v-icon {
      color: rgba(0, 0, 0, 0.4) !important;
    }
  }

  &__now-sub {
    margin-left: 4px;
    font-size: 10.5px;
    color: rgba(0, 0, 0, 0.35);
  }

  &__spark {
    width: 150px;
    height: 34px;
  }

  &__spark-label {
    font-size: 10.5px;
    color: rgba(0, 0, 0, 0.35);
  }

  &__funnel {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__step {
    flex: 1;
    min-width: 100px;
    background: #f8f9fb;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    padding: 10px 12px;
  }

  &__step-value {
    font-size: 20px;
    font-weight: 750;
    font-variant-numeric: tabular-nums;
  }

  &__step-label {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
  }

  &__step-delta {
    font-size: 11px;
    font-weight: 700;
    margin-left: 4px;
    vertical-align: 2px;

    &--up {
      color: #1b7f4d;
    }

    &--down {
      color: #c23434;
    }
  }

  &__step-rate {
    font-size: 11px;
    font-weight: 600;
    color: #27a950;
    margin-top: 2px;
    min-height: 14px; /* leere Rate: Platz reservieren, Boxen bleiben gleich hoch */
    white-space: pre; /* damit " " als Platzhalter nicht kollabiert */
  }
}

@keyframes live-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(39, 169, 80, 0.5);
  }
  55% {
    box-shadow: 0 0 0 6px rgba(39, 169, 80, 0);
  }
}
</style>
